import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { chatbotAdminApi } from '@/features/chatbot/api/chatbotApi';
import { getPlatformAdminErrorMessage } from '@/features/platform-admin/api/platformAdminApi';
import type { TenantEvoHubCredentialsStatus } from '@workspace/types';

type TenantEvoHubCredentialsCardProps = {
  tenantId?: string;
};

export function TenantEvoHubCredentialsCard({ tenantId }: TenantEvoHubCredentialsCardProps) {
  const [status, setStatus] = useState<TenantEvoHubCredentialsStatus | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const data = await chatbotAdminApi.getTenantEvoHubCredentials(tenantId);
      setStatus(data);
    } catch (error) {
      toast.error(getPlatformAdminErrorMessage(error, 'No se pudieron cargar credenciales Evo Hub'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [tenantId]);

  const handleSave = async () => {
    try {
      setSaving(true);
      const data = await chatbotAdminApi.updateTenantEvoHubCredentials(
        {
          apiKey: apiKey.trim() || undefined,
          webhookSecret: webhookSecret.trim() || undefined,
        },
        tenantId,
      );
      setStatus(data);
      setApiKey('');
      setWebhookSecret('');
      toast.success('Credenciales Evo Hub actualizadas');
    } catch (error) {
      toast.error(getPlatformAdminErrorMessage(error, 'Error al guardar credenciales Evo Hub'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Credenciales Evo Hub</CardTitle>
        <CardDescription>
          API Key y webhook secret por tenant para WhatsApp Cloud, Messenger e Instagram DM.
        </CardDescription>
      </CardHeader>
      <CardContent className='space-y-4'>
        {loading ? (
          <p className='text-sm text-muted-foreground'>Cargando…</p>
        ) : (
          <>
            <div className='text-sm'>
              Estado: {status?.configured ? 'Configurado' : 'Pendiente'}
              {status?.apiKeyLast4 ? ` · API Key …${status.apiKeyLast4}` : ''}
            </div>
            <div className='space-y-2'>
              <Label htmlFor='evohubApiKey'>API Key (evh_pk_…)</Label>
              <Input
                id='evohubApiKey'
                type='password'
                value={apiKey}
                onChange={event => setApiKey(event.target.value)}
                placeholder='evh_pk_…'
              />
            </div>
            <div className='space-y-2'>
              <Label htmlFor='evohubWebhookSecret'>Webhook secret</Label>
              <Input
                id='evohubWebhookSecret'
                type='password'
                value={webhookSecret}
                onChange={event => setWebhookSecret(event.target.value)}
                placeholder='Secreto HMAC compartido con Evo Hub'
              />
            </div>
            <Button type='button' onClick={() => void handleSave()} disabled={saving}>
              Guardar credenciales
            </Button>
          </>
        )}
      </CardContent>
    </Card>
  );
}
